/**
 * CardInput Component
 * Stripe Elements Card Input with card brand detection and validation
 */

import React, { useState } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'
import { CardElement } from '@stripe/react-stripe-js'

export interface CardInputProps {
  onError?: (error: string) => void
  onComplete?: () => void
  onChange?: (complete: boolean) => void
  className?: string
  disabled?: boolean
}

export const CardInput: React.FC<CardInputProps> = React.memo(function CardInput({
  onError,
  onComplete,
  onChange,
  className = '',
  disabled = false,
}) {
  const { t } = useTranslation('admin')
  const [error, setError] = useState<string | null>(null)
  const [complete, setComplete] = useState(false)
  const [cardBrand, setCardBrand] = useState<string | null>(null)

  const handleBlur = () => {
    // Validate on blur
    if (!complete && !error) {
      const errorMsg = t('superAdmin.orgAdmin.billing.paymentMethods.errors.incomplete')
      setError(errorMsg)
    }
  }

  const handleChange = (event: {
    complete: boolean
    error?: { message: string } | undefined
    brand?: string
  }) => {
    setComplete(event.complete)
    setError(event.error ? event.error.message : null)
    setCardBrand(event.brand ?? null)

    if (event.error && onError) {
      onError(event.error.message)
    } else if (event.complete) {
      if (onComplete) onComplete()
    }

    if (onChange) {
      onChange(event.complete)
    }
  }

  const getCardBrandIcon = () => {
    if (!cardBrand) return null

    const icons: Record<string, React.ReactNode> = {
      visa: (
        <svg className="w-6 h-4" viewBox="0 0 50 35" fill="none">
          <rect width="50" height="35" rx="4" fill="#1434CB" />
          <text x="25" y="22" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">
            VISA
          </text>
        </svg>
      ),
      mastercard: (
        <svg className="w-6 h-4" viewBox="0 0 50 35" fill="none">
          <circle cx="18" cy="17.5" r="12" fill="#EB001B" />
          <circle cx="32" cy="17.5" r="12" fill="#F79E1B" />
          <path
            d="M25 9.5C28.5 12 30.5 15.5 30.5 17.5C30.5 19.5 28.5 23 25 25.5C21.5 23 19.5 19.5 19.5 17.5C19.5 15.5 21.5 12 25 9.5Z"
            fill="#FF5F00"
          />
        </svg>
      ),
      amex: (
        <svg className="w-6 h-4" viewBox="0 0 50 35" fill="none">
          <rect width="50" height="35" rx="4" fill="#006FCF" />
          <text x="25" y="22" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">
            AMEX
          </text>
        </svg>
      ),
      discover: (
        <svg className="w-6 h-4" viewBox="0 0 50 35" fill="none">
          <rect width="50" height="35" rx="4" fill="#FF6600" />
          <text x="25" y="22" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">
            DISCOVER
          </text>
        </svg>
      ),
      diners: (
        <svg className="w-6 h-4" viewBox="0 0 50 35" fill="none">
          <rect width="50" height="35" rx="4" fill="#0079BE" />
          <text x="25" y="22" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">
            DINERS
          </text>
        </svg>
      ),
      jcb: (
        <svg className="w-6 h-4" viewBox="0 0 50 35" fill="none">
          <rect width="50" height="35" rx="4" fill="#0E4C96" />
          <text x="25" y="22" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">
            JCB
          </text>
        </svg>
      ),
    }

    return icons[cardBrand.toLowerCase()] ?? null
  }

  const cardElementOptions = {
    style: {
      base: {
        color: '#ffffff',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#6b7280',
        },
        iconColor: '#6b7280',
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444',
      },
      complete: {
        color: '#10b981',
        iconColor: '#10b981',
      },
    },
    disabled,
    hidePostalCode: false,
    iconStyle: 'solid' as const,
  }

  const cardDetailsLabel = t('superAdmin.orgAdmin.billing.paymentMethods.cardDetails')
  const secureNote = t('superAdmin.orgAdmin.billing.paymentMethods.secureNote')

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Card Brand Icons */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-crypto-text">{cardDetailsLabel}</label>
        <div className="flex items-center gap-2">
          {/* Always show brand icons */}
          <div className="flex gap-1 opacity-40">
            <svg className="w-8 h-5" viewBox="0 0 32 20" fill="currentColor">
              <rect
                x="0.5"
                y="0.5"
                width="31"
                height="19"
                rx="3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              />
            </svg>
          </div>
          {getCardBrandIcon()}
        </div>
      </div>

      {/* Card Element */}
      <div className={`relative ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <div
          className={`p-4 bg-crypto-dark border rounded-lg transition-colors ${
            error
              ? 'border-red-500/50 focus-within:border-red-500'
              : complete
                ? 'border-green-500/50 focus-within:border-green-500'
                : 'border-crypto-border focus-within:border-crypto-accent'
          }`}
        >
          <CardElement options={cardElementOptions} onBlur={handleBlur} onChange={handleChange} />
        </div>

        {/* Validation Indicator */}
        {complete && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg
              className="w-5 h-5 text-green-500 [html[data-theme=light]_&]:text-green-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-400 [html[data-theme=light]_&]:text-red-700">
          <svg
            className="w-4 h-4 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Security Note */}
      <p className="text-xs text-crypto-muted flex items-center gap-1">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        {secureNote}
      </p>
    </div>
  )
})

CardInput.displayName = 'CardInput'
